import React, { useRef, useEffect } from 'react';
import { AlertTriangle, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useFocusTrap } from '../../hooks/useFocusTrap';

export interface MessinessIssue {
  type: string;
  severity: 'red' | 'amber';
  message: string;
  suggestion: string;
  fixable: boolean;
}

export interface MessinessResult {
  needs_cleanup: boolean;
  confidence: number;
  reasons: string[];
  recommendation: 'cleanup' | 'review' | 'direct';
  file_type: string;
  issues: MessinessIssue[];
  details: Record<string, any>;
  filename: string;
}

interface MessinessCheckModalProps {
  results: MessinessResult[];
  isUploading: boolean;
  onProceedToCleanup: () => void;
  onSkipCleanup: () => void;
  onCancel: () => void;
}

export const MessinessCheckModal: React.FC<MessinessCheckModalProps> = ({
  results,
  isUploading,
  onProceedToCleanup,
  onSkipCleanup,
  onCancel,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const hasCleanupRecommendation = results.some(r => r.recommendation === 'cleanup');
  const hasIssues = results.some(r => r.issues.length > 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Document Quality Check" className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold">Document Quality Check</h2>
              <p className="text-orange-100 text-sm">We analyzed your files and found some issues</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Summary */}
          <div className="mb-6">
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span>{results.filter(r => r.issues.some(i => i.severity === 'red')).length} files need attention</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                <span>{results.filter(r => r.issues.some(i => i.severity === 'amber') && !r.issues.some(i => i.severity === 'red')).length} files with warnings</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>{results.filter(r => r.issues.length === 0).length} files OK</span>
              </div>
            </div>
          </div>
          
          {/* File-by-file results */}
          <div className="space-y-4">
            {results.map((result, idx) => (
              <div 
                key={idx}
                className={`rounded-lg border-2 p-4 ${
                  result.issues.some(i => i.severity === 'red') 
                    ? 'border-red-300 bg-red-50' 
                    : result.issues.length > 0 
                      ? 'border-amber-300 bg-amber-50'
                      : 'border-green-300 bg-green-50'
                }`}
              >
                {/* File header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <span className="font-medium text-gray-900">{result.filename}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-200 rounded text-gray-600">{result.file_type.toUpperCase()}</span>
                  </div>
                  {result.issues.length === 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : result.issues.some(i => i.severity === 'red') ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                </div>
                
                {/* Issues */}
                {result.issues.length > 0 ? (
                  <ul className="space-y-2">
                    {result.issues.map((issue, iIdx) => (
                      <li key={iIdx} className="text-sm">
                        <div className="flex items-start gap-2">
                          <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                            issue.severity === 'red' ? 'bg-red-500' : 'bg-amber-500'
                          }`}></span>
                          <div>
                            <span className={issue.severity === 'red' ? 'text-red-800' : 'text-amber-800'}>
                              {issue.message}
                            </span>
                            <p className="text-gray-600 text-xs mt-0.5">{issue.suggestion}</p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-green-700">No issues detected - file looks clean!</p>
                )}
              </div>
            ))}
          </div>
          
          {/* Recommendation */}
          {hasCleanupRecommendation && (
            <div className="mt-6 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-4">
              <h3 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                <span className="text-xl">🧹</span>
                Recommendation: Use BI Cleanup
              </h3>
              <p className="text-sm text-orange-800">
                Some of your files have data quality issues. BI Cleanup will extract, validate, 
                and clean the data with AI assistance and human review before importing.
              </p>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="border-t px-6 py-4 bg-gray-50 space-y-3">
          {(hasCleanupRecommendation || hasIssues) && (
            <button
              onClick={onProceedToCleanup}
              disabled={isUploading}
              className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:from-orange-400 disabled:to-red-400 text-white font-semibold rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Uploading to BI Cleanup...
                </>
              ) : (
                <>🧹 Use BI Cleanup (Recommended)</>
              )}
            </button>
          )}
          
          <button
            onClick={onSkipCleanup}
            disabled={isUploading}
            className={`w-full px-4 py-3 font-semibold rounded-lg transition-colors ${
              hasCleanupRecommendation || hasIssues
                ? 'border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50'
                : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400'
            }`}
          >
            {hasCleanupRecommendation || hasIssues
              ? 'Continue Anyway (May have errors)'
              : 'Continue with Upload'}
          </button>
          
          <button
            onClick={onCancel}
            disabled={isUploading}
            className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 text-sm disabled:opacity-50"
          >
            Cancel and choose different files
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessinessCheckModal;
